import { classNames } from '@geerd-nx/utils';
import { ExclamationCircleIcon } from '@heroicons/react/20/solid';
import { InputHTMLAttributes, forwardRef } from 'react';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  error?: string;
  help?: string;
  label: string;
  optional?: boolean;
  hiddenLabel?: boolean;
  LeadingIcon?: typeof ExclamationCircleIcon;
  containerClassName?: string;
  hideError?: boolean;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      containerClassName = 'w-full',
      className,
      type,
      help,
      error,
      optional,
      hiddenLabel,
      disabled,
      id,
      label,
      LeadingIcon,
      hideError,
      ...props
    },
    ref
  ) => {
    return (
      <div className={containerClassName}>
        <div className="flex justify-between">
          <label
            htmlFor={id}
            className={`block text-sm font-medium leading-6 text-gray-900 ${
              hiddenLabel ? 'sr-only' : ''
            }`}
          >
            {label}
          </label>
          {optional && (
            <span className="text-sm leading-6 text-gray-500" id="optional">
              Optional
            </span>
          )}
        </div>

        <div
          className={`relative ${
            hiddenLabel ? '' : 'mt-2'
          }  rounded-md shadow-sm`}
        >
          {LeadingIcon && (
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <LeadingIcon
                className="h-5 w-5 text-gray-400"
                aria-hidden="true"
              />
            </div>
          )}
          <input
            type={type}
            className={classNames(
              'block w-full rounded-md border-0 px-3 focus:outline-none py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500 disabled:ring-gray-200',
              error
                ? 'text-red-900 ring-red-300 placeholder:text-red-300 focus:ring-red-500'
                : '',
              disabled
                ? 'text-gray-900 ring-gray-300 placeholder:text-gray-400'
                : '',
              LeadingIcon ? 'pl-10' : '',
              className || ''
            )}
            ref={ref}
            disabled={disabled}
            id={id}
            {...props}
          />
          {error && (
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
              <ExclamationCircleIcon
                className="h-5 w-5 text-red-500"
                aria-hidden="true"
              />
            </div>
          )}
        </div>
        <div className="flex justify-between">
          {help && (
            <p className="mt-2 text-sm text-gray-500" id="email-description">
              {help}
            </p>
          )}
          <p
            className={`${
              error ? 'opacity-100' : 'opacity-0'
            } mt-0.5 h-6 text-xs text-red-600 ${hideError ? 'hidden' : ''}`}
          >
            {error}
          </p>
        </div>
      </div>
    );
  }
);
Input.displayName = 'Input';

export default Input;
